import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { FaCheckCircle, FaEnvelope, FaTicketAlt, FaCalendarAlt, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import LoadingPage from "../../components/Loading/LoadingPage";
import { getReservation, verifyPayment, requestRefund, isRefundEligible } from "../../services/ticketingService";
import { useAuth } from "../../contexts/AuthContext";
import { ttqTrack } from "../../utils/tiktok";
import { fbqTrack } from "../../utils/meta";
import "./ReservationConfirmation.css";

/**
 * Post-purchase confirmation page.
 * Route: /tickets/confirmation?reservation_id=...
 *
 * For free tickets: shows all tickets with QR codes.
 * For paid tickets: shows "check your email" message while Stripe webhook processes.
 */
export default function ReservationConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customerToken } = useAuth();

  const reservationId = searchParams.get("reservation_id");
  // Stripe appends session_id={CHECKOUT_SESSION_ID} to the success URL
  const sessionId = searchParams.get("session_id");

  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pollCount, setPollCount] = useState(0);
  const [verifyAttempted, setVerifyAttempted] = useState(false);
  const [pixelFired, setPixelFired] = useState(false);

  const [refundConfirming, setRefundConfirming] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!reservationId) {
      setError("No reservation ID provided.");
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const data = await getReservation(reservationId);
        if (mounted) {
          setReservation(data);
          setLoading(false);
        }
      } catch {
        if (mounted) {
          setError("Could not load reservation details.");
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [reservationId, pollCount]);

  // On first load: if pending + session_id present, call verify endpoint directly
  // (fallback for when the Stripe production webhook is delayed or misconfigured)
  useEffect(() => {
    if (!reservation || reservation.payment_status !== "pending") return;
    if (!sessionId || verifyAttempted) return;

    setVerifyAttempted(true);
    (async () => {
      try {
        await verifyPayment(reservationId, sessionId);
        // Reload reservation to pick up the newly confirmed state
        setPollCount((n) => n + 1);
      } catch {
        // Silent — fall back to polling; webhook may still arrive
      }
    })();
  }, [reservation, sessionId, verifyAttempted, reservationId]);

  // Poll every 3s while still pending (up to ~30s), as secondary fallback
  useEffect(() => {
    if (!reservation || reservation.payment_status !== "pending") return;
    if (pollCount >= 10) return; // stop after ~30s
    const timer = setTimeout(() => setPollCount((n) => n + 1), 3000);
    return () => clearTimeout(timer);
  }, [reservation, pollCount]);

  // TikTok: fire CompletePayment once when payment is confirmed
  // Uses reservation_id as event_id to deduplicate with the server-side Events API event
  useEffect(() => {
    if (!reservation || pixelFired) return;
    const status = reservation.payment_status;
    if (status !== "paid" && status !== "free") return;

    setPixelFired(true);
    ttqTrack(
      "CompletePayment",
      {
        value: (reservation.total_cents || 0) / 100,
        currency: "USD",
        contents: [
          {
            content_id: String(reservation.event_id),
            content_name: reservation.event_title || "Event",
            quantity: reservation.tickets?.length || 1,
          },
        ],
      },
      reservationId, // matches server-side event_id for deduplication
    );

    // Meta: Purchase — same reservation_id as event_id to deduplicate with server Conversions API event
    fbqTrack(
      "Purchase",
      {
        value: (reservation.total_cents || 0) / 100,
        currency: "USD",
        content_type: "product",
        content_ids: [String(reservation.event_id)],
        content_name: reservation.event_title || "Event",
        num_items: reservation.tickets?.length || 1,
      },
      reservationId, // matches server-side event_id for deduplication
    );
  }, [reservation, pixelFired, reservationId]);

  // Countdown redirect after successful refund
  useEffect(() => {
    if (!refundSuccess) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [refundSuccess, navigate]);

  const handleRefund = async () => {
    if (!reservation) return;
    setRefunding(true);
    setRefundError("");
    try {
      await requestRefund(reservation.reservation_id, {
        customerToken: customerToken || null,
        guestEmail: reservation.guest_email || null,
      });
      setRefundSuccess(true);
      setRefundConfirming(false);
      setReservation((prev) => ({ ...prev, payment_status: "refunded" }));
    } catch (err) {
      setRefundError(err.message || "Refund failed. Please try again.");
    } finally {
      setRefunding(false);
    }
  };

  if (loading) return <LoadingPage />;

  if (error) {
    return (
      <div className="rc-page"><div className="rc-container">
        <div className="rc-error">
          <p>{error}</p>
          <button onClick={() => navigate("/")} className="rc-btn rc-btn--secondary">
            Go Home
          </button>
        </div>
      </div></div>
    );
  }

  const isPaid = reservation.payment_status === "paid";
  const isFree = reservation.payment_status === "free";
  const isPending = reservation.payment_status === "pending";
  const isRefunded = reservation.payment_status === "refunded";

  // A reservation is refundable if any ticket passes isRefundEligible and the user can auth
  const canRefund = !refundSuccess &&
    isPaid &&
    (customerToken || reservation.guest_email) &&
    (reservation.tickets || []).some((t) => isRefundEligible({
      ...t,
      reservation_payment_status: reservation.payment_status,
    }));

  const TZ = "America/Chicago";
  const eventTitle = reservation.event_title || "Your Event";
  const eventDate = reservation.start_at
    ? new Date(reservation.start_at).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: TZ,
      })
    : null;

  // Time display: tier valid_from/valid_until → event start_at/end_at → ticket end_at
  const formatTime = (dt) => dt
    ? new Date(dt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ })
    : null;
  const firstTicket = reservation.tickets?.[0];
  const timeDisplay = (() => {
    const vf = formatTime(firstTicket?.valid_from) || formatTime(reservation.start_at);
    const vu = formatTime(firstTicket?.valid_until)
      || formatTime(reservation.end_at)
      || formatTime(firstTicket?.end_at);
    if (vf && vu && vf !== vu) return `${vf} – ${vu}`;
    return vf || vu || null;
  })();

  return (
    <>
    <div className="rc-page">
    <div className="rc-container">
      <div className="rc-card">
        {/* Success icon */}
        <div className={`rc-icon-wrap ${isPending ? "rc-icon-wrap--pending" : isRefunded ? "rc-icon-wrap--refunded" : "rc-icon-wrap--success"}`}>
          {isPending ? <FaEnvelope /> : <FaCheckCircle />}
        </div>

        {/* Heading */}
        <h1 className="rc-title">
          {isPending ? "Payment Processing…" : isRefunded ? "Refund Initiated" : "You're In!"}
        </h1>

        <p className="rc-subtitle">
          {isPending
            ? "Your payment is being processed. Tickets will be sent to your email once confirmed."
            : isRefunded
            ? "Your tickets have been cancelled and a refund is on its way to your original payment method."
            : isFree
            ? "Your free tickets are confirmed. Check your email for your QR codes."
            : "Your tickets are confirmed! Check your email for your QR codes."}
        </p>

        {/* Event info */}
        <div className="rc-event-info">
          <h2 className="rc-event-title">{eventTitle}</h2>
          {eventDate && (
            <div className="rc-event-detail">
              <FaCalendarAlt />
              <span>{eventDate}</span>
            </div>
          )}
          {timeDisplay && (
            <div className="rc-event-detail">
              <FaClock />
              <span>{timeDisplay}</span>
            </div>
          )}
          {reservation.venue_name && (
            <div className="rc-event-detail">
              <FaMapMarkerAlt />
              <span>{reservation.venue_name}</span>
            </div>
          )}
        </div>

        {/* Ticket list — show QR codes for confirmed tickets */}
        {(isPaid || isFree) && reservation.tickets?.length > 0 && (
          <div className="rc-tickets">
            <h3 className="rc-tickets-title">
              <FaTicketAlt /> Your Tickets
            </h3>
            <div className="rc-ticket-list">
              {reservation.tickets.map((ticket) => (
                <div key={ticket.ticket_id} className="rc-ticket-item">
                  <div className="rc-ticket-meta">
                    <span className="rc-ticket-tier">{ticket.tier_name}</span>
                    <span className="rc-ticket-name">{ticket.attendee_name || "Guest"}</span>
                  </div>
                  {ticket.qr_image_url ? (
                    <img
                      src={ticket.qr_image_url}
                      alt={`QR code for ticket ${ticket.ticket_code}`}
                      className="rc-qr-image"
                    />
                  ) : (
                    <div className="rc-qr-placeholder">
                      <FaTicketAlt />
                      <span>QR being generated…</span>
                    </div>
                  )}
                  <p className="rc-ticket-code">
                    Code: <code>{String(ticket.ticket_code).slice(0, 8)}…</code>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email note */}
        <div className="rc-email-note">
          <FaEnvelope />
          <p>A confirmation email with your QR codes has been sent to your inbox. Show your QR code at the door.</p>
        </div>

        {/* Refund success + countdown */}
        {refundSuccess && (
          <div className="rc-refund-success">
            <FaCheckCircle />
            <span>Refund initiated. You'll receive a confirmation email shortly.</span>
          </div>
        )}
        {refundSuccess && countdown !== null && (
          <div className="rc-redirect-bar">
            <div className="rc-redirect-bar-inner">
              <div className="rc-redirect-progress" style={{ animationDuration: "5s" }} />
              <span className="rc-redirect-text">
                Redirecting to home in <strong>{countdown}</strong>s…
              </span>
              <button className="rc-redirect-now" onClick={() => navigate("/")}>
                Go now
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="rc-actions">
          {customerToken ? (
            <Link to={`/my-tickets?customer_token=${customerToken}`} className="rc-btn rc-btn--primary">
              <FaTicketAlt /> View My Tickets
            </Link>
          ) : null}
          {reservation.event_slug && (
            <button
              onClick={() => navigate(`/events/${reservation.event_slug}/tickets`)}
              className="rc-btn rc-btn--secondary"
            >
              <FaTicketAlt /> Buy More Tickets
            </button>
          )}
          <button onClick={() => navigate("/")} className="rc-btn rc-btn--secondary">
            Back to Home
          </button>
        </div>

        {/* Refund link */}
        {canRefund && (
          <div className="rc-refund-section">
            <button
              className="rc-refund-link"
              onClick={() => { setRefundConfirming(true); setRefundError(""); }}
            >
              Can't make it? Request a refund.
            </button>
          </div>
        )}
      </div>
    </div>
    </div>

    {/* Refund confirmation modal */}
    {refundConfirming && (
      <div className="rc-modal-overlay" onClick={() => { if (!refunding) { setRefundConfirming(false); setRefundError(""); } }}>
        <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
          <h3 className="rc-modal-title">Request a Refund</h3>
          <p className="rc-modal-body">
            This will cancel all tickets in this order and refund your payment.
          </p>
          {refundError && <p className="rc-refund-error">{refundError}</p>}
          <div className="rc-modal-btns">
            <button
              className="rc-btn rc-btn--primary"
              onClick={handleRefund}
              disabled={refunding}
            >
              {refunding ? "Processing…" : "Yes, refund my tickets"}
            </button>
            <button
              className="rc-btn rc-btn--secondary"
              onClick={() => { setRefundConfirming(false); setRefundError(""); }}
              disabled={refunding}
            >
              Keep my tickets
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
